import API from './client'
import type { User } from '../types'

export interface UsersFilter {
  role?: 'livreur' | 'expediteur' | 'admin'
  search?: string
  status?: 'active' | 'suspended'
  page?: number
  limit?: number
}

export interface UserWithStats {
  _id: string
  name: string
  phone: string
  email?: string
  role: string
  plan?: string
  company?: { name?: string; taxId?: string; address?: string }
  vehicle?: { type?: string; brand?: string; model?: string; plate?: string }
  isOnline?: boolean
  isVerified?: boolean
  isSuspended?: boolean
  suspensionReason?: string
  rating?: number
  totalDeliveries?: number
  parcelCount?: number
  commissionFixe?: number
  fraisLivraison?: number
  createdAt: string
}

export interface UsersListResponse {
  users: UserWithStats[]
  pagination: { total: number; page: number; limit: number; totalPages: number }
}

export interface UserDetailResponse {
  user: UserWithStats
  stats: {
    total: number
    delivered: number
    failed: number
    cancelled: number
    totalSpent: number
  }
}

export const usersAPI = {
  // ─── Existing methods ─────────────────────────────────────────────────────────

  getLivreurs: (params?: {
    page?: number
    limit?: number
    search?: string
    isOnline?: boolean
    isVerified?: boolean
  }) => API.get('/users/livreurs', { params }),

  verifyLivreur: (id: string) => API.patch(`/users/${id}/verify`),

  deactivateLivreur: (id: string) => API.patch(`/users/${id}/deactivate`),

  setAvailability: (isOnline: boolean) => API.patch('/users/availability', { isOnline }),

  getExpediteurs: (params?: { page?: number; limit?: number; search?: string; plan?: string }) =>
    API.get('/users/expediteurs', { params }),

  getExpediteursList: () => API.get('/users/expediteurs/list'),

  getMe: async (): Promise<User> => {
    const res = await API.get('/auth/me')
    return res.data.user
  },

  updateMe: (data: Record<string, unknown>) => API.patch('/users/me', data),

  // ─── Admin CRUD ───────────────────────────────────────────────────────────────

  getAll: async (filters: UsersFilter = {}): Promise<UsersListResponse> => {
    const r = await API.get('/users', { params: filters })
    return r.data
  },

  getById: async (id: string): Promise<UserDetailResponse> => {
    const r = await API.get(`/users/${id}`)
    return r.data
  },

  update: async (id: string, data: Partial<UserWithStats>): Promise<UserWithStats> => {
    const r = await API.patch(`/users/${id}`, data)
    return r.data
  },

  setSuspended: async (id: string, suspended: boolean, reason?: string): Promise<UserWithStats> => {
    const r = await API.patch(`/users/${id}/suspend`, { suspended, reason })
    return r.data
  },

  delete: async (id: string): Promise<void> => {
    await API.delete(`/users/${id}`)
  },

  createExpediteur: async (data: Record<string, unknown>): Promise<UserWithStats> => {
    const r = await API.post('/users', { ...data, role: 'expediteur' })
    return r.data
  },

  setCommission: async (id: string, commissionFixe: number): Promise<void> => {
    await API.patch(`/users/${id}/commission`, { commissionFixe })
  },

  setFraisLivraison: async (id: string, fraisLivraison: number): Promise<void> => {
    await API.patch(`/users/${id}/frais-livraison`, { fraisLivraison })
  },

  activate: async (id: string): Promise<UserWithStats> => {
    const r = await API.patch(`/users/${id}`, { isActive: true })
    return r.data
  },

  changePassword: async (id: string, password: string): Promise<void> => {
    await API.patch(`/users/${id}/password`, { password })
  },
}
